import cn from 'classnames'
import React, { Fragment, useCallback, useMemo } from 'react'

import config from '@config'
import ancillaryUtils from '@lib/ancillary'
import bem from '@lib/bem'
import connectionUtils from '@lib/connection'
import { useSeatAncillaryContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatAncillary'
import { OptionalSeatSelectionToggle } from '@pages/Checkout/SeatSelection/NewSeats/OptionalSeatSelectionToggle'
import { SegmentSeats } from '@pages/Checkout/SeatSelection/NewSeats/SegmentSeats'
import { useParams } from '@stores/params'

import '@pages/Checkout/SeatSelection/NewSeats/ConnectionSeats.scss'

interface ConnectionSeatsProps {
  connection: Connection
  direction: Seat.TripDirection
  fareClass: string
}

export const ConnectionSeats: React.FC<ConnectionSeatsProps> = ({ connection, direction, fareClass }) => {
  const [{ marketingCarrierCode }] = useParams()
  const isAncillary = marketingCarrierCode ? config.seatAncillaryAsSelection?.[marketingCarrierCode] : false
  const { ancillaries, globalAncillaries, isAncillaryActive, activateAncillary, deactivateAncillary } =
    useSeatAncillaryContext()
  const relevantAncillaries = useMemo(
    () =>
      ancillaries.filter(
        ancillary =>
          !globalAncillaries.includes(ancillary) &&
          connection.segments.some(connectionSegment =>
            ancillary.segments?.some(ancillarySegment =>
              connectionUtils.isSameSegment(connectionSegment, ancillarySegment),
            ),
          ),
      ),
    [ancillaries, globalAncillaries, connection.segments],
  )
  const optionalRelevantAncillaries = relevantAncillaries.filter(ancillary => !ancillaryUtils.isMandatory(ancillary))
  const showOptionalToggle = Boolean(isAncillary && optionalRelevantAncillaries.length)

  const handleConnectionToggleChange = useCallback(
    (active: boolean) =>
      optionalRelevantAncillaries.forEach(ancillary => {
        active ? activateAncillary(ancillary) : deactivateAncillary(ancillary)
      }),
    [optionalRelevantAncillaries, activateAncillary, deactivateAncillary],
  )

  return (
    <div data-tag={`new-seats-connection-${direction}`} className={cn(bem('new-seats', 'connection'), 'column')}>
      {showOptionalToggle && (
        <OptionalSeatSelectionToggle
          context="connection"
          ancillaries={relevantAncillaries}
          enabled={optionalRelevantAncillaries.some(isAncillaryActive)}
          onChange={handleConnectionToggleChange}
        />
      )}
      {connection.segments.map((segment, index) => (
        <Fragment key={segment.id}>
          <SegmentSeats
            connection={connection}
            segment={segment}
            direction={direction}
            connectionFareClass={fareClass}
          />
          {index < connection.segments.length - 1 && <hr aria-hidden={true} />}
        </Fragment>
      ))}
    </div>
  )
}
